import React, { useState, useEffect, useRef, useCallback } from 'react';
import cx from 'classnames';
import SiteLink from 'components/shared/sitelink';
import { checkIsWindowsOS, countryCodeEmoji, filterCountriesByName } from 'lib/utils/helpers';
import { useUI, useCustomer, useLogoutCustomer } from 'hooks';
// import { useRouter } from 'next/router';
import { useSettings, useGlobalModules } from 'contexts';
import { keys, set } from 'lib/utils/localStorage';
import styles from './localeSelector.module.scss';

const LocaleSelector = ({ locale, allLocales, pageData }) => {
  const { countryCode, setCountryCode } = useSettings();
  const { countrySelector } = useGlobalModules()?.translations || {};
  const mainButton = useRef(null);
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);

  const [selectedLocale, setSelectedLocale] = useState(locale);
  const [countrySelectorIsOpen, setCountrySelectorIsOpen] = useState(false);
  const [isWindows, setIsWindows] = useState('');
  const [query, setQuery] = useState('');

  const { openLocalePopup } = useUI();

  // const router = useRouter();
  const { customer } = useCustomer();
  const [logoutCustomer] = useLogoutCustomer(locale);
  const filteredLocales = allLocales.filter(Boolean);

  // Utility functions.
  const getLocaleFromSlug = slug => slug?.replace(/\//g, '')?.toLowerCase();
  const getCleanName = name => name?.split('|')?.[0]?.trim();
  const getCountryName = useCallback(() => {
    // if el.slug === na set the default to US because both Canada and US have the same slug
    const currentLocale = filteredLocales.find(el => {
      // if selected in locale selector
      if (selectedLocale === 'CA') {
        return el.countryCode === selectedLocale;
      }
      if (selectedLocale === 'BE' && locale === 'fr-be') {
        return el.slug === '/fr-be/';
      }
      if (selectedLocale === 'BE' && locale === 'nl-be') {
        return el.slug === '/nl-be/';
      }
      // if was set from locale
      if (selectedLocale === 'na') {
        return el.countryCode === 'US';
      }
      if (selectedLocale === 'de') {
        return el.countryCode === 'DE';
      }

      return getLocaleFromSlug(el?.slug) === selectedLocale || el.countryCode === selectedLocale;
    })?.name;

    return currentLocale || '';
  }, [filteredLocales, selectedLocale, locale]);

  useEffect(() => {
    setIsWindows(checkIsWindowsOS());
  }, []);

  useEffect(() => {
    if (countrySelectorIsOpen && searchRef.current && window.matchMedia('(hover: hover)').matches) {
      searchRef.current.focus();
    }
  }, [countrySelectorIsOpen]);

  const openDropdown = () => {
    setQuery('');
    setCountrySelectorIsOpen(true);
  };

  const closeDropdown = () => {
    setCountrySelectorIsOpen(false);
    mainButton.current.focus(); // Return focus to the button
  };

  // Close menu on Escape key
  useEffect(() => {
    const handleKeyDown = e => {
      if (countrySelectorIsOpen && e.key === 'Escape') {
        closeDropdown();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [countrySelectorIsOpen, setCountrySelectorIsOpen]);

  // todo change countryCode and selectedLocale to use sys.id
  useEffect(() => {
    if (countryCode !== selectedLocale) {
      setSelectedLocale(countryCode.toLowerCase() === locale ? countryCode : locale);
    }
  }, [countryCode, locale]);

  if (!allLocales) return null;

  const visibleLocales = filterCountriesByName(filteredLocales, query);

  const isCountryAndLanguageLocale = cc => cc?.length >= 2 && cc.length <= 6;
  const getCountryFromLocale = cc => cc?.slice(-2);
  const getEmoji = cc =>
    isCountryAndLanguageLocale(cc) &&
    countryCodeEmoji(cc === 'fr-fr' ? 'tn' : getCountryFromLocale(cc)); // changing fr-fr to tn(Tunisia)

  const handleLanguageChange = (e, slug, cc) => {
    setCountrySelectorIsOpen(false);

    const localeFromSlug = getLocaleFromSlug(slug === '/' ? 'en' : slug);
    setSelectedLocale(cc);
    setCountryCode(cc);

    set(keys.USER_LOCALE_PREFERENCE, localeFromSlug);

    if (slug === '/' && localeFromSlug === 'en') {
      openLocalePopup(slug);
    } else {
      const pageSlug = pageData.href.find(
        ({ locale: hrefLocale }) => hrefLocale === localeFromSlug
      );
      const baseUrl = document.location.origin;
      const path = pageSlug?.slug?.startsWith('/') ? pageSlug?.slug : `/${pageSlug?.slug || ''}`;
      const newLocale = localeFromSlug === 'en' ? '' : `/${localeFromSlug}`;
      const destination = `${newLocale}${path}`;

      if (customer) {
        logoutCustomer(customer?.customerAccessToken?.accessToken, destination);
      }
      // New-auth logout hard-navigates via Shopify's end-session endpoint, which
      // redirects to `destination`; a second navigation here would race it.
      if (customer?.authType === 'newAccounts') return;

      document.location.href = `${baseUrl}${newLocale}${path}`;
    }
  };

  return (
    <nav className={styles.countrySelector} role="navigation" aria-label="Locale navigation">
      <button
        type="button"
        onClick={() => openDropdown()}
        className={styles.selected}
        aria-label="Select your country"
        ref={mainButton}
      >
        <span role="img" aria-label={getCleanName(getCountryName())} className={styles.flag}>
          {getEmoji(selectedLocale)}
        </span>
        <span className={styles.label}>{getCountryName() || ''}</span>
      </button>
      <div
        className={cx(styles.dropdownBackdrop, { [styles.visible]: countrySelectorIsOpen })}
        onClick={() => closeDropdown()}
        tabIndex={-1}
        role="none"
      />
      {countrySelectorIsOpen && (
        <div
          className={cx(styles.dropdown, { [styles.visible]: countrySelectorIsOpen })}
          aria-modal="true"
          role="dialog"
          tabIndex={-1}
          ref={dropdownRef}
        >
          <input
            ref={searchRef}
            className={styles.search}
            placeholder={countrySelector?.searchPlaceholder || 'Search'}
            aria-label={countrySelector?.searchPlaceholder || 'Search'}
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {visibleLocales.map(({ externalLink, name, slug, countryCode: cc, sys }) => {
            if (externalLink === null) {
              return (
                <div
                  key={sys?.id}
                  className={styles.localeContainer}
                  onClick={e => handleLanguageChange(e, slug, cc)}
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleLanguageChange(e, slug, cc);
                    }
                  }}
                  role="button"
                  aria-label={getCleanName(name)}
                  name={name}
                >
                  {!isWindows && (
                    <span role="img" aria-label={getCleanName(name)} className={styles.flag}>
                      {getEmoji(cc)}
                    </span>
                  )}
                  {name}
                </div>
              );
            }
            return (
              <SiteLink
                key={sys?.id}
                external={`${externalLink}`}
                className={styles.localeContainer}
                onClick={() => closeDropdown()}
                aria-label={getCleanName(name)}
              >
                {!isWindows && (
                  <span role="img" aria-label={getCleanName(name)} className={styles.flag}>
                    {getEmoji(cc)}
                  </span>
                )}
                {name}
              </SiteLink>
            );
          })}
          {query.trim() && visibleLocales.length === 0 && (
            <div className={styles.noResults}>{countrySelector?.noMatches || 'No matches'}</div>
          )}
        </div>
      )}
    </nav>
  );
};

export default LocaleSelector;
